import React from "react";
import { useNavigate } from "react-router-dom";
import { useBilling } from "../context/BillingContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Hammer,
  Zap,
  Lock,
  Check,
  FileText,
  BarChart2,
  Clock,
  FolderOpen,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";

const FEATURES = [
  { icon: Clock, label: "Timeline & milestone tracking" },
  { icon: BarChart2, label: "Budget & financial dashboard" },
  { icon: FolderOpen, label: "Document & invoice storage" },
  { icon: FileText, label: "Room-by-room material management" },
];

export default function PaywallOverlay({ open, reason = "", onClose }) {
  const navigate = useNavigate();
  const { billing } = useBilling();

  const lowerReason = reason.toLowerCase();
  const isPaymentFailed =
    lowerReason.includes("payment failed") ||
    billing?.subscription_status === "past_due" ||
    billing?.subscription_status === "unpaid";
  const isTrialExpired = lowerReason.includes("trial");

  let headline, subline, ctaLabel, accentColor;

  if (isPaymentFailed) {
    headline = "Your last payment failed";
    subline = "Access is paused until your payment goes through. Update your payment method in Settings to restore access right away.";
    ctaLabel = "Update payment";
    accentColor = "from-red-500 via-red-400 to-red-600";
  } else if (isTrialExpired) {
    headline = "Your free trial has ended";
    subline = "Subscribe now to keep every material decision, milestone, and document right where you left them.";
    ctaLabel = "Choose a plan";
    accentColor = "from-amber-500 via-amber-400 to-amber-600";
  } else {
    headline = "Your subscription is inactive";
    subline = "Reactivate your plan to get back into your projects instantly.";
    ctaLabel = "Reactivate now";
    accentColor = "from-blue-500 via-blue-400 to-blue-600";
  }

  const goToPricing = () => {
    onClose();
    setTimeout(() => navigate(isPaymentFailed ? "/settings" : "/pricing"), 250);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-2xl border-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Paywall</DialogTitle>
        </DialogHeader>

        {/* Top accent bar */}
        <div className={`h-1.5 bg-gradient-to-r ${accentColor}`} />

        <div className="p-8">
          {/* Logo + lock icon */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="bg-blue-600 p-2.5 rounded-xl shadow-lg shadow-blue-200">
              <Hammer className="h-5 w-5 text-white" />
            </div>
            <div className={`${isPaymentFailed ? "bg-red-100 border-red-200" : "bg-amber-100 border-amber-200"} border p-2.5 rounded-xl`}>
              {isPaymentFailed
                ? <AlertTriangle className="h-5 w-5 text-red-600" />
                : <Lock className="h-5 w-5 text-amber-600" />}
            </div>
          </div>

          {/* Headline */}
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">
            {headline}
          </h2>
          <p className="text-slate-500 text-sm text-center leading-relaxed mb-7">
            {subline}
          </p>

          {/* Data safety reassurance */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-3.5 mb-6">
            <div className="flex items-start gap-2.5">
              <ShieldCheck className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
              <div className="text-xs text-green-800 leading-relaxed">
                <strong>Your data is 100% safe.</strong> All projects, materials,
                documents, and budgets are preserved — nothing is ever deleted.
                Subscribe any time to pick up right where you left off.
              </div>
            </div>
          </div>

          {/* Feature reminder */}
          <ul className="space-y-2.5 mb-8">
            {FEATURES.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="flex items-center gap-3 text-sm text-slate-600">
                <div
                  className="w-7 h-7 bg-blue-50 border border-blue-100 rounded-lg
                  flex items-center justify-center shrink-0">
                  <Icon className="h-3.5 w-3.5 text-blue-600" />
                </div>
                {label}
              </li>
            ))}
          </ul>

          {/* CTA */}
          <Button
            onClick={goToPricing}
            className={`w-full text-white font-semibold h-11 text-sm gap-2 shadow-lg mb-3
              ${isPaymentFailed
                ? "bg-red-600 hover:bg-red-500 shadow-red-100"
                : "bg-blue-600 hover:bg-blue-500 shadow-blue-100"}`}>
            <Zap className="h-4 w-4" />
            {isPaymentFailed ? ctaLabel : <>{ctaLabel}, $39/mo</>}
          </Button>

          {/* Secondary: dismiss */}
          <button
            onClick={onClose}
            className="w-full text-slate-400 hover:text-slate-600 text-xs py-1.5 transition-colors">
            Maybe later
          </button>

          {/* Trust note */}
          <p className="text-center text-xs text-slate-400 mt-4">
            <Check className="inline h-3 w-3 mr-1 text-green-500" />
            Cancel any time. No long-term contracts. Powered by Stripe.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
